import { Injectable } from '@angular/core';
import { DropdownActions, ModalActions } from '../enums/base.enums';

@Injectable({
  providedIn: 'root'
})
export class BootstrapService {

  // Bootstrap refuses show() and hide() outright while a modal is part-way through a transition
  // (`if (!this._isShown || this._isTransitioning) return`) and says nothing about having done so.
  // A dialog answered during its own ~300ms fade-in therefore stays on screen, and the next click
  // runs its handler a second time against state the first click already consumed. These hold the
  // last action asked for while an element is animating, to apply when the animation ends.
  private readonly animatingModals = new WeakSet<HTMLElement>();
  private readonly queuedModalActions = new WeakMap<HTMLElement, ModalActions>();
  private readonly watchedModals = new WeakSet<HTMLElement>();

  constructor() { }

   /**
   * Utility method to safely manage Bootstrap modals
   * @param element - CSS selector or DOM element for the modal
   * @param action - Action to perform: 'show', 'hide', 'toggle', or 'dispose'
   * @returns The modal instance or null if element not found
   */
  public manageModal(element: string | HTMLElement, action: ModalActions) {
    try {
      // Check if Bootstrap Modal is available
      const win = window as any;
      if (!win.bootstrap || !win.bootstrap.Modal) {
        console.error('Bootstrap Modal is not available on window.bootstrap.Modal');
        return null;
      }

      // Deferred rather than dropped: Bootstrap would ignore this outright mid-transition.
      const target = BootstrapService.resolveElement(element);

      if (target && (action === ModalActions.Show || action === ModalActions.Hide)) {
        this.watchModalTransitions(target);

        if (this.animatingModals.has(target)) {
          this.queuedModalActions.set(target, action);
          return null;
        }

        this.queuedModalActions.delete(target);
      }

      // Get existing modal instance
      let modal = win.bootstrap.Modal.getInstance(element);

      // If no existing instance and action is not 'dispose', create a new one
      if (!modal && action !== ModalActions.Dispose) {
        modal = new win.bootstrap.Modal(element);
      }
      
      // Perform the requested action
      if (modal) {
        switch (action) {
          case ModalActions.Show:
            modal.show();
            break;
          case ModalActions.Hide:
            modal.hide();
            break;
          case ModalActions.Toggle:
            modal.toggle();
            break;
          case ModalActions.Dispose:
            modal.dispose();
            modal = null;
            break;
        }
      }
      
      return modal;
    } catch (error) {
      console.error('Error managing modal:', error);
      return null;
    }
  }

  /**
   * Hands one modal over to another so only one is ever on screen.
   *
   * `hide()` returns immediately but Bootstrap finishes ~300ms later, and its closing cleanup then
   * strips `body.modal-open` and the scrollbar padding it had added — tearing those away from the
   * modal shown in the meantime, so the page behind an open dialog scrolls and shifts. Waiting for
   * `hidden.bs.modal` keeps the two lifecycles from overlapping. Shows straight away when the modal
   * being handed over is not on screen, since no hide event is coming.
   */
  private static resolveElement(element: string | HTMLElement): HTMLElement | null {
    if (typeof element !== 'string') return element;

    // querySelector answers with the first match and says nothing about the rest, so a modal left in
    // the page by a destroyed component quietly takes every later action aimed at its live twin —
    // which shows as a dialog with no content and buttons that do nothing, far from the cause.
    const matches = document.querySelectorAll<HTMLElement>(element);

    if (matches.length > 1) {
      console.warn(`More than one element matches '${element}'. Acting on the first, which may be a `
        + `modal a destroyed component left behind. Modal ids must be unique in the document.`);
    }

    return matches[0] ?? null;
  }

  // One set of listeners per element, for as long as that element lives. Both weak collections are
  // keyed by the element itself, so nothing here keeps a removed modal alive.
  private watchModalTransitions(element: HTMLElement): void {
    if (this.watchedModals.has(element)) return;
    this.watchedModals.add(element);

    const began = () => this.animatingModals.add(element);

    const ended = () => {
      this.animatingModals.delete(element);

      const queued = this.queuedModalActions.get(element);
      if (queued === undefined) return;

      this.queuedModalActions.delete(element);
      this.manageModal(element, queued);
    };

    element.addEventListener('show.bs.modal', began);
    element.addEventListener('hide.bs.modal', began);
    element.addEventListener('shown.bs.modal', ended);
    element.addEventListener('hidden.bs.modal', ended);
  }

  public swapModals(hideElement: string | HTMLElement, showElement: string | HTMLElement): void {
    const hideTarget = BootstrapService.resolveElement(hideElement);

    // Painted means shown OR part-way through a transition either way: Bootstrap drops `.show` as a
    // hide starts but only sets `display: none` when it ends, and sets `display: block` partway into
    // a show — so a modal still coming up reads as hidden by its style alone.
    const isOnScreen = !!hideTarget && hideTarget.isConnected
      && (this.animatingModals.has(hideTarget) || getComputedStyle(hideTarget).display !== 'none');

    if (hideTarget && isOnScreen) {
      hideTarget.addEventListener(
        'hidden.bs.modal',
        () => this.manageModal(showElement, ModalActions.Show),
        { once: true }
      );
    }

    // Harmless when a hide is already under way — Bootstrap ignores a second call mid-transition.
    this.manageModal(hideElement, ModalActions.Hide);

    // Nothing to wait for: no hidden event is coming.
    if (!isOnScreen) this.manageModal(showElement, ModalActions.Show);
  }

  /**
   * Sweeps orphaned modal backdrops left behind when a modal's host element is removed without
   * Bootstrap's hide/dispose lifecycle running — e.g. the browser Back button unmounting the page
   * while a modal is open. Self-narrows to the true orphan case: if any `.modal.show` is still on
   * the page (a freshly-mounted route opened its own modal), its backdrop is left untouched.
   */
  public cleanupOrphanedModals(): void {
    if (document.querySelector('.modal.show')) return;
    document.querySelectorAll('.modal-backdrop').forEach(el => el.remove());
    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
  }

  /**
   * Closes an Actions menu that was held open by its own item so an in-flight spinner stayed
   * visible — Bootstrap only closes a menu on a click that reaches the document, so a caller that
   * stops that click has to close the menu itself once the work settles. No-ops without a toggle.
   */
  public hideDropdownIfPresent(toggle?: HTMLElement): void {
    if (!toggle) return;

    this.manageDropdown(toggle, DropdownActions.Hide);
  }

  /**
   * Utility method to safely manage Bootstrap dropdowns
   * @param element - CSS selector or DOM element for the dropdown
   * @param action - Action to perform: 'show', 'hide', 'toggle', or 'dispose'
   * @returns The dropdown instance or null if element not found
   */
  public manageDropdown(element: string | HTMLElement, action: DropdownActions) {
    try {
      // Check if Bootstrap Dropdown is available
      const win = window as any;
      if (!win.bootstrap || !win.bootstrap.Dropdown) {
        console.error('Bootstrap Dropdown is not available on window.bootstrap.Dropdown');
        return null;
      }

      // Get existing dropdown instance
      let dropdown = win.bootstrap.Dropdown.getInstance(element);
      
      // If no existing instance and action is not 'dispose', create a new one
      if (!dropdown && action !== DropdownActions.Dispose) {
        dropdown = new win.bootstrap.Dropdown(element);
      }
      
      // Perform the requested action
      if (dropdown) {
        switch (action) {
          case DropdownActions.Show:
            dropdown.show();
            break;
          case DropdownActions.Hide:
            dropdown.hide();
            break;
          case DropdownActions.Toggle:
            dropdown.toggle();
            break;
          case DropdownActions.Dispose:
            dropdown.dispose();
            dropdown = null;
            break;
        }
      }
      
      return dropdown;
    } catch (error) {
      console.error('Error managing dropdown:', error);
      return null;
    }
  }

}
